export interface NoticeImage {
  src: string;
  alt: string;
}

export interface ParsedNoticeContent {
  text: string;
  images: NoticeImage[];
}

const isSafeImageSource = (source: string): boolean => {
  if (/^data:image\/(?:gif|jpeg|png|webp);base64,[a-z0-9+/]+={0,2}$/i.test(source)) return true;
  try {
    const url = new URL(source, window.location.origin);
    return url.protocol === 'http:' || url.protocol === 'https:';
  } catch {
    return false;
  }
};

/** Extracts displayable text and safe images without rendering arbitrary editor HTML. */
export const parseNoticeContent = (value: string): ParsedNoticeContent => {
  if (typeof DOMParser === 'undefined') {
    return {
      text: value.replace(/<[^>]*>/g, '').trim(),
      images: [],
    };
  }

  const document = new DOMParser().parseFromString(value, 'text/html');
  document.querySelectorAll('br').forEach((element) => element.replaceWith('\n'));
  document.querySelectorAll('p, div, li, blockquote, h1, h2, h3, h4, h5, h6').forEach((element) => {
    element.append(document.createTextNode('\n'));
  });

  const images = [...document.querySelectorAll('img')].flatMap((image) => {
    const src = image.getAttribute('src')?.trim() ?? '';
    if (!src || !isSafeImageSource(src)) return [];
    return [{ src, alt: image.getAttribute('alt')?.trim() || 'Notice image' }];
  });

  return {
    text: (document.body.textContent ?? '').replace(/[\t ]*\n[\t ]*/g, '\n').replace(/\n{3,}/g, '\n\n').trim(),
    images: [...new Map(images.map((image) => [image.src, image])).values()],
  };
};
